/**
 * El capítulo del arancel con la estructura del libro (tarea A3 del vault 127).
 *
 * El problema que resuelve: navegar un capítulo devolvía una lista PLANA, y el
 * arancel está lleno de descripciones como "Los demás" que solo significan algo
 * colgadas de su rama. En la lista plana aparecían cuatro "Los demás" seguidas
 * sin decir demás de qué (vault 126 §3.1).
 *
 * ⚖️ Los niveles (los `-`, `--`, `---` del libro) son TÍTULOS y NO llevan
 * alícuota: solo las partidas de verdad la tienen. Poner un `0%` donde el libro
 * deja la celda vacía sería inventar un dato — y en un arancel eso se cotiza.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { tariffCatalogService, type NodoArancel } from '../services/tariff-catalog-service';

/** Píxeles de sangría por nivel de profundidad. */
const SANGRIA_POR_NIVEL = 18;

/** Los guiones con los que el propio arancel marca la profundidad. */
function guiones(nivel: number) {
  return nivel > 0 ? '—'.repeat(nivel) : '';
}

function Fila({
  nodo,
  onElegirPartida,
  resaltar,
}: {
  nodo: NodoArancel;
  onElegirPartida: (code: string) => void;
  /** Códigos a marcar: las partidas que aparecieron en la búsqueda (C2, plan 178). */
  resaltar?: Set<string>;
}) {
  const [abierto, setAbierto] = useState(true);
  const esNivel = nodo.tipo === 'nivel';
  const marcada = !esNivel && nodo.code != null && resaltar?.has(nodo.code) === true;

  return (
    <>
      <tr
        className={
          esNivel
            ? 'bg-muted/40 italic text-muted-foreground cursor-pointer hover:bg-muted/60'
            : marcada
              ? 'cursor-pointer bg-primary/10 font-medium hover:bg-primary/15'
              : 'cursor-pointer hover:bg-muted/30'
        }
        onClick={() => (esNivel ? setAbierto((v) => !v) : onElegirPartida(nodo.code!))}
      >
        <td className="w-[140px] px-3 py-1.5 align-top font-mono text-xs">{nodo.code ?? ''}</td>
        <td
          className="px-3 py-1.5 text-sm"
          style={{ paddingLeft: 12 + nodo.nivel * SANGRIA_POR_NIVEL }}
        >
          {esNivel && (
            <span className="mr-1 inline-block align-middle">
              {abierto ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
          )}
          <span className="mr-1 opacity-50">{guiones(nodo.nivel)}</span>
          {nodo.texto}
        </td>
        {/* Solo las hojas llevan alícuota. Los niveles van vacíos, como el libro. */}
        <td className="w-[70px] px-3 py-1.5 text-right text-sm">
          {esNivel ? '' : `${nodo.gaRate}%`}
        </td>
        <td className="w-[70px] px-3 py-1.5 text-right text-sm">
          {esNivel ? '' : `${nodo.ivaRate}%`}
        </td>
      </tr>
      {abierto &&
        nodo.hijos.map((h) => (
          <Fila
            key={`${h.code ?? h.texto}-${h.nivel}`}
            nodo={h}
            onElegirPartida={onElegirPartida}
            resaltar={resaltar}
          />
        ))}
    </>
  );
}

export function ArbolArancel({
  capitulo,
  onElegirPartida,
  resaltar,
}: {
  capitulo: string;
  onElegirPartida: (code: string) => void;
  /** Códigos a marcar dentro del árbol: las partidas de la búsqueda activa. */
  resaltar?: Set<string>;
}) {
  const [arbol, setArbol] = useState<NodoArancel[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // `vivo` corta el seteo de estado si el usuario cambia de capítulo antes de
    // que llegue la respuesta: sin esto, la del capítulo viejo pisa a la nueva.
    let vivo = true;
    setCargando(true);
    setError(false);
    tariffCatalogService
      .arbolCapitulo(capitulo)
      .then((r) => {
        if (vivo) setArbol(r.arbol ?? []);
      })
      .catch(() => {
        if (vivo) setError(true);
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [capitulo]);

  if (cargando) return <p className="text-sm text-muted-foreground">Cargando el capítulo…</p>;
  // Un error de red no puede verse igual que un capítulo vacío: son dos cosas
  // distintas y el usuario tiene que saber si conviene reintentar.
  if (error)
    return (
      <p className="text-sm text-muted-foreground">
        No pudimos traer el capítulo. Probá de nuevo en un momento.
      </p>
    );
  if (arbol.length === 0)
    return (
      <p className="text-sm text-muted-foreground">Este capítulo no tiene partidas cargadas.</p>
    );

  return (
    <div className="max-h-[560px] overflow-auto rounded-md border">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 bg-background shadow-[inset_0_-1px_0_hsl(var(--border))]">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold">Código</th>
            <th className="px-3 py-2 text-left text-xs font-semibold">Descripción</th>
            <th className="px-3 py-2 text-right text-xs font-semibold">GA</th>
            <th className="px-3 py-2 text-right text-xs font-semibold">IVA</th>
          </tr>
        </thead>
        <tbody>
          {arbol.map((n) => (
            <Fila
              key={`${n.code ?? n.texto}-${n.nivel}`}
              nodo={n}
              onElegirPartida={onElegirPartida}
              resaltar={resaltar}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
